import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { supportsTransparentMov } from 'lib/utils/supports-transparent-mov';

/* 

Renders a video element that supports transparent backgrounds with hydration-safe fallback.
- Server-side: Always renders the video element
- Client-side: Checks browser support for transparent .mov files
- If unsupported: Switches to image fallback after hydration
- Uses suppressHydrationWarning to prevent React warnings during the intentional switch

*/

interface TransparentVideoProps
  extends React.VideoHTMLAttributes<HTMLVideoElement> {
  fallbackSrc: string;
  alt: string;
  ref: React.Ref<HTMLVideoElement>;
}

export const TransparentVideo: React.FC<TransparentVideoProps> = ({
  fallbackSrc,
  alt,
  src,
  width,
  height,
  loop = true,
  muted = true,
  playsInline = true,
  ...videoProps
}) => {
  const [showVideo, setShowVideo] = useState(true);

  useEffect(() => {
    const supportsTransparent = supportsTransparentMov();

    if (!supportsTransparent) {
      setShowVideo(false);
    }
  }, []);

  if (showVideo) {
    return (
      <video
        src={src}
        width={width}
        height={height}
        loop={loop}
        muted={muted}
        playsInline={playsInline}
        {...videoProps}
      />
    );
  }

  return (
    <Image
      src={fallbackSrc}
      alt={alt}
      width={typeof width === 'number' ? width : parseInt(width || '0')}
      height={typeof height === 'number' ? height : parseInt(height || '0')}
      className={videoProps.className}
      data-testid={videoProps['data-testid']}
    />
  );
};

export default TransparentVideo;
